import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Paper,
  LinearProgress,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import { useSearchParams } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { getMigrationProgress, getMigrationLatest, getMigrationSearch, postMigrationCdnLookup, uploadMigrationExcel } from '../../services/apiService';
import { useDropzone } from 'react-dropzone';
import { format } from 'date-fns';
import { getCurrentUser } from '../../services/authService';
import { dispatchMigrationDataUpdated, onMigrationDataUpdated } from './migrationRefreshEvents';

const AZURE_COLOR = '#1D4434';
const COLO_COLOR = '#D74B00';

/** Any logged-in user may upload migration Excel */
const canUpload = () => !!getCurrentUser();

function hostFromUrl(u) {
  if (!u || typeof u !== 'string') return '';
  try {
    const s = u.trim();
    const url = s.startsWith('http') ? s : `https://${s}`;
    return new URL(url).hostname.toLowerCase();
  } catch {
    return '';
  }
}

/**
 * MigrationDashboardTab - Upload migration Excel (any logged-in user) and progress cards linking to Azure/CIAM/ISheet tabs
 */
const MigrationDashboardTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [progress, setProgress] = useState(null);
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchDone, setSearchDone] = useState(false);
  const [cdnMap, setCdnMap] = useState({});
  const [cdnLookupBusy, setCdnLookupBusy] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [progressRes, latestRes] = await Promise.all([
        getMigrationProgress(),
        getMigrationLatest()
      ]);
      if (progressRes?.success && progressRes?.data) {
        setProgress({ ...progressRes.data, meta: progressRes.meta });
        if (progressRes.meta?._timings) {
          console.log('[Migration Dashboard] progress API timings (ms):', progressRes.meta._timings);
        }
      } else {
        setProgress(null);
      }
      if (latestRes?.success) setLatest(latestRes.data || null);
    } catch (err) {
      console.error('Migration dashboard load error:', err);
      setError(err.message || 'Failed to load migration data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => onMigrationDataUpdated(() => loadData()), [loadData]);

  const onDrop = useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;
      try {
        setUploading(true);
        setError(null);
        setUploadSuccess(null);
        const res = await uploadMigrationExcel(file);
        if (res?.success) {
          setUploadSuccess(`Uploaded ${res.data?.rowCount ?? 0} rows from ${res.data?.fileName ?? 'file'}.`);
          loadData();
          dispatchMigrationDataUpdated({
            rowCount: res.data?.rowCount,
            fileName: res.data?.fileName,
            uploadedAt: res.data?.uploadedAt
          });
        } else {
          setError(res?.message || 'Upload failed');
        }
      } catch (err) {
        setError(err.message || 'Upload failed');
      } finally {
        setUploading(false);
      }
    },
    [loadData]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1,
    disabled: !canUpload() || uploading
  });

  const goToTab = (tabIndex) => {
    const tabNames = ['dashboard', 'azure', 'azure-stats', 'azure-perf', 'ciam', 'isheet'];
    const next = new URLSearchParams(searchParams);
    next.set('tab', tabNames[tabIndex]);
    setSearchParams(next);
  };

  const handleQuickSearch = useCallback(async () => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults([]);
      setCdnMap({});
      setCdnLookupBusy(false);
      setSearchDone(true);
      return;
    }
    setSearchLoading(true);
    setSearchDone(false);
    setCdnMap({});
    setCdnLookupBusy(false);
    try {
      const res = await getMigrationSearch(q, 100);
      const data = res?.data ?? [];
      setSearchResults(data);
      setSearchDone(true);
      const hosts = [...new Set(data.map((r) => hostFromUrl(r.instanceUrl)).filter(Boolean))];
      if (hosts.length > 0) {
        setCdnLookupBusy(true);
        postMigrationCdnLookup(hosts)
          .then((cdnRes) => {
            if (cdnRes?.success && cdnRes?.map) setCdnMap(cdnRes.map);
          })
          .catch(() => {})
          .finally(() => setCdnLookupBusy(false));
      }
    } catch (err) {
      console.error('Migration search error:', err);
      setSearchResults([]);
      setSearchDone(true);
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery]);

  const total = progress?.meta?.rowCount ?? 0;
  const azureMigrated = progress?.azure?.migratedCount ?? 0;
  const azureNew = progress?.azure?.newCount ?? 0;
  const azureTotalWith = (progress?.azure?.totalWithInitiative ?? (azureMigrated + azureNew)) || 0;
  const ciamMigrated = progress?.ciam?.migratedCount ?? 0;
  const ciamNew = progress?.ciam?.newCount ?? 0;
  const ciamTotalWith = (progress?.ciam?.totalWithInitiative ?? (ciamMigrated + ciamNew)) || 0;
  const isheetMigrated = progress?.isheet?.migratedCount ?? 0;
  const isheetNew = progress?.isheet?.newCount ?? 0;
  const isheetTotalWith = (progress?.isheet?.totalWithInitiative ?? (isheetMigrated + isheetNew)) || 0;

  // Azure vs Colo pie chart: Azure = current datacenter starts with "z", rest = Colo (from API clientsByHosting)
  const clientsByHosting = progress?.clientsByHosting || { azure: 0, colo: 0 };
  const azureCount = Number(clientsByHosting.azure ?? 0) || 0;
  const coloCount = Number(clientsByHosting.colo ?? 0) || 0;
  const azureVsColoTotal = azureCount + coloCount;
  const azureVsColoData = [
    { name: 'Azure', value: azureCount, count: azureCount },
    { name: 'Colo', value: coloCount, count: coloCount }
  ];

  const cardItems = [
    { title: 'Azure Migration', totalLabel: 'Total Clients on Azure', migrated: azureMigrated, new: azureNew, totalWith: azureTotalWith, total, tabIndex: 1, color: '#1D4434' },
    { title: 'CIAM Migration', totalLabel: 'Total Clients on CIAM', migrated: ciamMigrated, new: ciamNew, totalWith: ciamTotalWith, total, tabIndex: 4, color: '#D74B00' },
    { title: 'Backend ISheet Modernization Migration', totalLabel: 'Total Clients on Backend Modernized ISheet', migrated: isheetMigrated, new: isheetNew, totalWith: isheetTotalWith, total, tabIndex: 5, color: '#2E7D32' }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={280} p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {uploadSuccess && (
        <Alert severity="success" onClose={() => setUploadSuccess(null)} sx={{ mb: 2 }}>
          {uploadSuccess}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom fontWeight={600} sx={{ color: AZURE_COLOR }}>
          Quick search
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Search by client name or URL to see migration status and dates.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
          <TextField
            size="small"
            placeholder="Client name or URL"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleQuickSearch()}
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              )
            }}
          />
          <Button variant="contained" onClick={handleQuickSearch} disabled={searchLoading} startIcon={searchLoading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />} sx={{ bgcolor: AZURE_COLOR, '&:hover': { bgcolor: '#153328' } }}>
            {searchLoading ? 'Searching…' : 'Search'}
          </Button>
        </Box>
        {searchDone && (
          <>
            {searchResults.length === 0 ? (
              <Typography color="text.secondary">No clients found. Enter client name or URL to search.</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Client name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>URL</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>CDN</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Datacenter</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Azure</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>CIAM</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Modernized ISheet</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchResults.map((row, idx) => {
                      const initiativeCell = (onInitiative, type, migrationDate, registrationDate, notOnLabel) => {
                        if (!onInitiative) return notOnLabel;
                        const fmt = (v) => {
                          if (!v) return '—';
                          if (typeof v === 'string') {
                            const iso = v.slice(0, 10);
                            const [y, m, day] = iso.split('-').map(Number);
                            if (y && m && day) return format(new Date(y, m - 1, day), 'd-MMM-yyyy');
                          }
                          const dt = new Date(v);
                          if (Number.isNaN(dt.getTime())) return '—';
                          return format(new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()), 'd-MMM-yyyy');
                        };
                        if (type === 'new') return `Newly onboarded (${fmt(registrationDate)})`;
                        return `Migrated (${fmt(migrationDate)})`;
                      };
                      const host = hostFromUrl(row.instanceUrl);
                      const lookupDone = !host || Object.prototype.hasOwnProperty.call(cdnMap, host);
                      const detected = host && lookupDone ? cdnMap[host] : null;
                      let cdnDisplay;
                      if (!host) {
                        cdnDisplay = '—';
                      } else if (cdnLookupBusy && !lookupDone) {
                        cdnDisplay = <CircularProgress size={14} sx={{ display: 'block' }} />;
                      } else {
                        cdnDisplay = detected === 'Cloudflare' || detected === 'Akamai' ? detected : 'No CDN';
                      }
                      return (
                        <TableRow key={idx}>
                          <TableCell>{row.instanceName || '—'}</TableCell>
                          <TableCell>
                            {row.instanceUrl ? (
                              <Link href={row.instanceUrl} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {row.instanceUrl}
                              </Link>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>{cdnDisplay}</TableCell>
                          <TableCell>{row.currentDatacenter || '—'}</TableCell>
                          <TableCell>
                            {initiativeCell(row.azureMigrated, row.azureType, row.azureMigrationDate, row.registrationDate, 'Not on Azure')}
                          </TableCell>
                          <TableCell>
                            {initiativeCell(row.ciamMigrated, row.ciamType, row.ciamMigrationDate, row.registrationDate, 'Not on CIAM')}
                          </TableCell>
                          <TableCell>
                            {initiativeCell(row.isheetMigrated, row.isheetType, row.isheetMigrationDate, row.registrationDate, 'Not on Modernized ISheet')}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Paper>

      {canUpload() && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight={600}>
            Upload migration Excel
          </Typography>
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'divider',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: isDragActive ? 'action.hover' : 'background.default',
              cursor: canUpload() && !uploading ? 'pointer' : 'default'
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary">
              {uploading ? 'Uploading…' : isDragActive ? 'Drop the file here' : 'Drag & drop an Excel file here, or click to select'}
            </Typography>
            {uploading && <LinearProgress sx={{ mt: 2, maxWidth: 320, mx: 'auto' }} />}
          </Box>
          {latest?.uploadedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Last upload: {format(new Date(latest.uploadedAt), 'PPpp')} — {latest.fileName} ({latest.rowCount} rows)
            </Typography>
          )}
        </Paper>
      )}

      <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1, display: 'block', mb: 2 }}>
        Progress by initiative
      </Typography>
      <Grid container spacing={2}>
        {cardItems.map((item) => {
          const pct = item.total > 0 && item.totalWith > 0 ? Math.round((item.totalWith / item.total) * 100) : 0;
          return (
            <Grid item xs={12} sm={6} md={4} key={item.title}>
              <Card
                onClick={() => goToTab(item.tabIndex)}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 },
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box sx={{ height: 4, bgcolor: item.color }} />
                <CardContent sx={{ pt: 2, pb: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: item.color, mb: 1.5 }}>
                    {item.title.replace(' Migration', '').replace('Backend ISheet Modernization', 'Backend ISheet')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'baseline', mb: 0.5 }}>
                    <Typography component="span" variant="h4" fontWeight={700} sx={{ color: item.color }}>
                      {item.totalWith.toLocaleString()}
                    </Typography>
                    <Typography component="span" variant="body2" color="text.secondary">
                      of {item.total.toLocaleString()} · {pct}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {item.migrated.toLocaleString()} migrated · {item.new.toLocaleString()} new
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {total > 0 && (
        <Paper sx={{ p: 2, mt: 3, maxWidth: 420 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ color: AZURE_COLOR }}>
            Azure vs Colo
          </Typography>
          {azureVsColoTotal > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={azureVsColoData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ name, value }) => {
                    const n = Number(value) || 0;
                    const pct = azureVsColoTotal > 0 ? Math.round((n / azureVsColoTotal) * 100) : 0;
                    return `${name}: ${n.toLocaleString()} (${pct}%)`;
                  }}
                  labelLine
                >
                  {azureVsColoData.map((entry) => (
                    <Cell key={entry.name} fill={entry.name === 'Azure' ? AZURE_COLOR : COLO_COLOR} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => {
                  const n = Number(value) || 0;
                  const pct = azureVsColoTotal > 0 ? Math.round((n / azureVsColoTotal) * 100) : 0;
                  return [`${n.toLocaleString()} (${pct}%)`, 'Clients'];
                }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Box py={3} textAlign="center" color="text.secondary">
              No Azure vs Colo data yet.
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default MigrationDashboardTab;
